// Shared station live-status logic so every surface (station header, site
// overview cards, health summary) agrees on what "online / offline / inactive"
// means — and, crucially, keeps telemetry visible for a deactivated station
// that is still transmitting.

export type StationLiveStatus = 'online' | 'offline' | 'inactive';

export const STATION_FRESHNESS_MS = 5 * 60 * 1000;

// Whether the station reported within the freshness window. Independent of the
// `active` admin flag: a deactivated station can still be sending syncs.
export const hasFreshData = (station: { updatedAt?: string | Date | null }): boolean =>
  !!station.updatedAt && new Date(station.updatedAt).getTime() >= Date.now() - STATION_FRESHNESS_MS;

// Three-state status. An admin-deactivated station is "inactive" (not
// "offline"), even while it keeps transmitting; only a station we've genuinely
// lost contact with is "offline".
export const getStationLiveStatus = (
  station: { active?: boolean; updatedAt?: string | Date | null },
): StationLiveStatus => {
  if (!station.active) return 'inactive';
  return hasFreshData(station) ? 'online' : 'offline';
};

// Voltage is only flagged out of range when we actually have a fresh reading to
// trust (< 9V or >= 17V). A stale reading is shown as unknown, not as a fault.
export const isVoltageOutOfRange = (voltage?: number | null): boolean => {
  const v = voltage ?? 0;
  return v < 9 || v >= 17;
};
